import { useEffect, useState } from 'react';
import { useFlavorVersions, useVersionDetail, useRestoreVersion } from '@/hooks/useFlavorVersions';
import { Button } from '@/components/primitives/Button';
import { logEvent } from '@/lib/eventLogger';
import type { VersionDetail } from '@/utils/types/flavorVersion';

type Props = {
  flavorId: string;
  formulaId: number;
  onClose: () => void;
  onRestoreSuccess?: (detail: VersionDetail) => void;
};

// ── Time helpers ─────────────────────────────────────────────────────────────
// Relative time for the version-list rows. Defaults to short, casual labels;
// the row's `title` attribute surfaces the exact timestamp on hover.

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  const diff = now.getTime() - t;

  if (diff < MS_PER_MINUTE) return 'just now';
  if (diff < MS_PER_HOUR) {
    const m = Math.floor(diff / MS_PER_MINUTE);
    return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diff < MS_PER_DAY) {
    const h = Math.floor(diff / MS_PER_HOUR);
    return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  }
  // Within the same calendar yesterday (US locale "yesterday")
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (t >= startOfToday - MS_PER_DAY && t < startOfToday) return 'yesterday';

  const days = Math.floor(diff / MS_PER_DAY);
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} ${w === 1 ? 'week' : 'weeks'} ago`;
  }
  // For older entries: short calendar form (Mon DD).
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatExactTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

export function HistoryPopover({ flavorId, formulaId, onClose, onRestoreSuccess }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const versions = useFlavorVersions(flavorId, formulaId);
  const detail = useVersionDetail(flavorId, formulaId, selectedId);
  const restore = useRestoreVersion(flavorId, formulaId);

  const all = versions.data?.pages.flat() ?? [];

  // Fire once per popover open AFTER the first version page loads so the
  // analytics carries the actual visible count (not 0 from initial render).
  const [historyEventFired, setHistoryEventFired] = useState(false);
  useEffect(() => {
    if (historyEventFired) return;
    if (versions.isLoading) return;
    if (versions.isError) return;
    setHistoryEventFired(true);
    logEvent("flavor_workbench_history_viewed", {
      flavor_id: flavorId,
      formula_id: formulaId,
      version_count: all.length,
    });
  }, [historyEventFired, versions.isLoading, versions.isError, all.length, flavorId, formulaId]);

  if (versions.isLoading) {
    return (
      <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--ink-4)' }}>
        Loading history…
      </div>
    );
  }
  if (versions.isError) {
    return (
      <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--brand-amber)' }}>
        Failed to load history.
      </div>
    );
  }
  if (all.length === 0) {
    return (
      <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.5 }}>
        No history yet — your edits will be saved automatically.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', maxHeight: 360 }}>
      {/* Left: version list */}
      <div
        style={{
          width: 220,
          overflowY: 'auto',
          borderRight: '1px solid var(--line)',
          flexShrink: 0,
        }}
      >
        <div className="vstack" style={{ gap: 1, padding: 6 }}>
          {all.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setSelectedId(v.id)}
              className="hstack"
              style={{
                width: '100%',
                padding: '7px 8px',
                border: 0,
                borderRadius: 4,
                background:
                  selectedId === v.id ? 'var(--bg-sunk)' : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                gap: 6,
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--ink)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                v#{v.versionNumber}
                {' · '}
                {v.commitMessage ??
                  (v.saveType === 'autosave'
                    ? 'Autosave'
                    : v.saveType === 'named'
                    ? 'Named save'
                    : 'Manual save')}
              </span>
              <span
                className="mono"
                style={{ fontSize: 10, color: 'var(--ink-4)' }}
                title={formatExactTime(v.createdAt)}
              >
                {formatRelativeTime(v.createdAt)} · {v.itemCount} items
              </span>
            </button>
          ))}
          {versions.hasNextPage && (
            <button
              type="button"
              onClick={() => void versions.fetchNextPage()}
              disabled={versions.isFetchingNextPage}
              style={{
                width: '100%',
                padding: '7px 8px',
                border: 0,
                borderRadius: 4,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 11,
                color: 'var(--ink-4)',
                textAlign: 'center',
                opacity: versions.isFetchingNextPage ? 0.5 : 1,
              }}
            >
              {versions.isFetchingNextPage ? 'Loading…' : 'Load older…'}
            </button>
          )}
        </div>
      </div>

      {/* Right: preview pane.
          overflowX: hidden — the wider popover (720px) gives the table
          enough room that horizontal scrolling shouldn't occur. Long
          Name values ellipsize; the Identifier column stays compact;
          Amount + Unit sit on the right. */}
      <div
        style={{
          flex: 1,
          padding: '10px 14px',
          overflowY: 'auto',
          overflowX: 'hidden',
          minWidth: 0,
        }}
      >
        {selectedId == null && (
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
            Select a version to preview.
          </div>
        )}
        {detail.isLoading && (
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
        )}
        {detail.isError && (
          <div style={{ fontSize: 12, color: 'var(--brand-amber)' }}>
            Failed to load version.
          </div>
        )}
        {detail.data && (
          <>
            <div
              style={{
                fontSize: 12,
                color: 'var(--ink-3)',
                marginBottom: 8,
              }}
            >
              v#{detail.data.versionNumber} · {detail.data.itemCount} ingredients
            </div>
            <table
              style={{
                width: '100%',
                fontSize: 11,
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
              }}
            >
              <colgroup>
                {/* Name absorbs remaining space; Identifier/Amount/Unit fixed. */}
                <col />
                <col style={{ width: 120 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 56 }} />
              </colgroup>
              <thead>
                <tr style={{ color: 'var(--ink-4)' }}>
                  <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 4 }}>Name</th>
                  <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 4 }}>Identifier</th>
                  <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Amount</th>
                  <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 4, paddingLeft: 8 }}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {detail.data.items.map((i, idx) => (
                  <tr key={idx}>
                    <td
                      title={i.substanceName}
                      style={{
                        color: 'var(--ink)',
                        paddingBottom: 3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        paddingRight: 8,
                      }}
                    >
                      {i.substanceName}
                    </td>
                    <td
                      className="mono"
                      title={i.identifier ?? undefined}
                      style={{
                        color: 'var(--ink-3)',
                        paddingBottom: 3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {i.identifier ?? '—'}
                    </td>
                    <td
                      className="mono"
                      style={{
                        textAlign: 'right',
                        color: 'var(--ink-3)',
                        paddingBottom: 3,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {i.amountValue != null ? i.amountValue : '—'}
                    </td>
                    <td
                      className="mono"
                      style={{
                        color: 'var(--ink-4)',
                        paddingBottom: 3,
                        paddingLeft: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {i.amountValue != null ? (i.amountUnit ?? '') : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button
              size="xs"
              style={{ marginTop: 10, width: '100%' }}
              disabled={restore.isPending}
              onClick={() =>
                restore.mutate(detail.data!.id, {
                  onSuccess: (restored) => {
                    onClose();
                    onRestoreSuccess?.(restored);
                  },
                })
              }
            >
              {restore.isPending ? 'Restoring…' : 'Restore to this version'}
            </Button>
            {restore.isError && (
              <div
                className="mono"
                style={{ fontSize: 10, color: 'var(--brand-amber)', marginTop: 4 }}
              >
                Restore failed. Try again.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
